import $ from "jquery";

import BaseVirtualLineModal from "./base_virtual_line_modal";
import NewVirtualLineForm from "./new_virtual_line_form";
import NpaSelectionForm from "./npa_selection_form";

import Loading from "shared/loading";
import Ajax from "shared/ajax";

import Collection from "backdraft-app/src/collection";

export default class NewVirtualLineModal extends BaseVirtualLineModal {
  get title() { return "New Promo Number"; }

  initialize(config) {
    super.initialize(config);

    this.config = config;
    this.newUrl = this.config.newVirtualLineUrl;
    this.localNumberInventoryUrl = this.config.localNumberInventoryUrl;
    this.successCallback = this.config.successCallback;
    this.model = config.model;
    this._resetModel({ owner: this.model.get('owner') });

    this.newVirtualLineForm = new NewVirtualLineForm({ model: this.model, config: this.config });
    this.npaSelectionForm = new NpaSelectionForm({
      model: this.model,
      collection: new Collection([])
    });
    this.form = this.newVirtualLineForm;

    this.listenTo(this.model, "change:promo_type", this._updateButtons.bind(this));
  }

  buttons() {
    return [
      {
        text: "Cancel",
        className: "btn-secondary",
        click: this._onSecondaryClick.bind(this)
      },
      {
        text: "Save",
        className: "btn-primary",
        click: this._onPrimaryClick.bind(this)
      }
    ];
  }

  render(...args) {
    super.render(...args);
    this._updateButtons();
    return this;
  }

  _updateButtons() {
    if (this.form === this.npaSelectionForm) {
      this.$("button.btn-primary.btn").text("Submit");
      this.$("button.btn-secondary.btn").text("Back");
    } else {
      this.$("button.btn-primary.btn").text(this._localSelectedAndLookupAvailable() ? "Continue" : "Save");
      this.$("button.btn-secondary.btn").text("Cancel");
    }
  }

  _onPrimaryClick() {
    if (this._localSelectedAndLookupAvailable() && this.form !== this.npaSelectionForm) {
      this._onContinue();
    } else {
      this._onSave();
    }
  }

  _onSecondaryClick() {
    if (this.form === this.npaSelectionForm) {
      this.form = this.newVirtualLineForm;
      this.model.set("errors", null);
      this.render();
    } else {
      this.close();
    }
  }

  _onContinue() {
    const paramErrors = this._validateParamPresence();
    if (paramErrors.length > 0) {
      this._setErrorAndRender(paramErrors);
      return;
    }

    const localNumberInventoryParams = { location: this.model.get("number_pool_location") };
    if (this.model.get("number_pool_radius_checked")) {
      $.extend(localNumberInventoryParams, { radius: this.model.get("number_pool_search_radius") });
    }

    this.configureLoadingMask();

    $.ajax({
      type: "GET",
      url: this._addParamsToLocalNumberInventoryUrl(localNumberInventoryParams),
      complete() {
        Loading.hide();
      },
      success: this._onContinueSuccess.bind(this),
      error: (response, status) => {
        this._setErrorAndRender([Ajax.parseResponse(response).message]);
      }
    });
  }

  _addParamsToLocalNumberInventoryUrl(localNumberInventoryParams) {
    const inventoryUrlSplit = this.localNumberInventoryUrl.split("?");
    if (inventoryUrlSplit.length > 1) {
      return `${inventoryUrlSplit[0]}?${$.param(localNumberInventoryParams)}&${inventoryUrlSplit[1]}`;
    } else {
      return `${inventoryUrlSplit[0]}?${$.param(localNumberInventoryParams)}`;
    }
  }

  _validateParamPresence() {
    const errors = this._validateTargetAreaLocatorParamPresence();

    if (this.config.manageVirtualLines.requireMediaType && !this.model.get("media_type")) {
      errors.push("Media type must be selected.");
    } else if (this.model.get("media_type") === this.config.googleCallExtensionMediaType && !this.model.get("adwords_account_id")) {
      errors.push("Media type 'Google Call Extension' requires a Google Ads account be set.");
    }

    if (this.config.manageVirtualLines.displayCampaignSelector && !this.model.get("advertiser_campaign_id")) {
      errors.push("Invoca Campaign must be selected.");
    }

    return errors;
  }

  _validateTargetAreaLocatorParamPresence() {
    const errors = [];

    if (this._localSelectedAndLookupAvailable()) {
      if (!this.model.get("number_pool_location")) {
        errors.push("Target area is required.");
      }
    }

    if (this.model.get("number_pool_radius_checked") && !this.model.get("number_pool_search_radius")) {
      errors.push("Radius option is checked but no distance was given.");
    }

    return errors;
  }

  _onContinueSuccess({ display_location: displayLocation, npa_quantities: npaQuantities }, status) {
    this.model.set("errors", []);
    this.model.set("number_pool_location", displayLocation);

    const npaCollection = new Collection(npaQuantities.map(({ npa, quantity }) => {
      const npaModelDetails = { id: npa, name: npa };
      if (quantity < 10) {
        $.extend(npaModelDetails, { warningContent: `As of last night, we had ${quantity} numbers for this area code. We cannot promise you will able to get a number for this area.` });
      }

      return npaModelDetails;
    }));

    npaCollection.models[0].set("checked", true);

    this.npaSelectionForm.updateCollection(npaCollection);
    this.form = this.npaSelectionForm;
    this.render();
  }

  _onSave() {
    this.model.set("errors", []);
    this.render();
    this.configureLoadingMask();

    if (this.form === this.npaSelectionForm && !this._storeNpaRadioSelection()) {
      return;
    }

    this.model.save({}, {
      url: this.newUrl,
      complete() {
        Loading.hide();
      },
      success: () => {
        this.onRequestSuccess("Successfully added new promo number.");
      },
      error: this._onSaveError.bind(this)
    });
  }

  _storeNpaRadioSelection() {
    const selectedNpa = this.form.getSelection();
    if (selectedNpa) {
      const whitelist = this._parseWhitelist(selectedNpa.get("id"));
      this.model.set("number_pool_whitelist", whitelist);
      return true;
    } else {
      let errorMessage = "Please select an area code";
      if (this.form.getCollection().every(npaModel => npaModel.get("disabled"))) {
        errorMessage = "Please provide another target area.";
      }
      this._setErrorAndRender([errorMessage]);
      return false;
    }
  }

  _parseWhitelist(selectedNpa) {
    const numberPoolLocation = this.model.get('number_pool_location');

    if (numberPoolLocation) {
      const matches = numberPoolLocation.match(`^(${selectedNpa})-?([\\d]{3})$`);

      if (matches) {
        return `${matches[1]}-${matches[2]}`;
      }
    }

    return selectedNpa;
  }

  _onSaveError(model, xhr, options) {
    const errorMessages = Ajax.errorMessages(xhr);

    if (xhr.responseJSON && xhr.responseJSON.tn_prefixes && this.form === this.npaSelectionForm) {
      this.form.disableUnavailableNpasAndCheckNextAvailable(xhr.responseJSON.tn_prefixes);
      if (this.form.getCollection().every(npaModel => npaModel.get("disabled"))) {
        errorMessages.push("Please provide another target area.");
      }
    } else {
      this.form = this.newVirtualLineForm;
    }

    Ajax._responseJSON(xhr).deferred_order
      ? this._toastErrorAndCloseModal(errorMessages)
      : this._setErrorAndRender(errorMessages);
  }

  _localSelectedAndLookupAvailable() {
    return this.config.manageVirtualLines.numberSupport.lookup &&
      !this.config.manageVirtualLines.numberSupport.lookLike &&
      this.model.get("promo_type") === "local";
  }
}
